<template>
  <div v-if="isVisible" class="usage-meter">
    <div
      v-for="row in rows"
      :key="row.key"
      class="meter-row"
      :data-test="row.remaining <= 2 ? 'meter-warning' : 'meter-row'"
      :class="{ warning: row.remaining <= 2 }"
    >
      <font-awesome-icon v-if="row.remaining <= 2" icon="triangle-exclamation" />
      <span>{{ $t(row.labelKey, { n: row.remaining, m: row.limit }) }}</span>
    </div>
    <div class="meter-reset">{{ $t('usageMeter.resets', { date: resetDate }) }}</div>
  </div>
</template>

<script>
import { ref as vueRef, computed, watch, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { getDatabase, ref, onValue } from "firebase/database";
import { authStore } from "@/store/auth";
import { pricingStore } from "@/store/pricing.js";
import {
  fetchMonthlyUsage,
  getCurrentYearMonthInTimezone,
} from "@/composables/useUsageCounts.js";

export default {
  name: "UsageMeter",
  setup() {
    const { locale } = useI18n();
    // Monthly limits, or null when the user has none (free tier / signed out).
    const limits = vueRef(null);
    const timezone = vueRef(null);
    const usage = vueRef(null);
    const failed = vueRef(false);

    // The listener follows authStore.userId: at mount the uid may not have
    // resolved yet, and it changes again on sign-out/sign-in. The usage
    // fan-read over dialogs/{uid} only runs once the account proves a paid
    // tier (free users would fetch the whole subtree for a meter that never
    // renders), and a token discards fetches that resolve after the uid
    // changed underneath them.
    let off = null;
    let fetchToken = 0;
    const stopUserWatch = watch(
      () => authStore.userId,
      (userId) => {
        if (off) {
          off();
          off = null;
        }
        fetchToken++;
        limits.value = null;
        usage.value = null;
        failed.value = false;
        timezone.value = null;
        if (!userId) return;
        let usageRequested = false;
        let fetchedTimezone = null;
        off = onValue(
          ref(getDatabase(), `users/${userId}`),
          (snap) => {
            const data = snap.val() || {};
            timezone.value = data.timezone || null;
            // Server precedence (users.checkPaidUserLimit): explicit monthly
            // features, else the plan identified by the billing lookup key —
            // legacy paid rows can predate the monthly-feature backfill.
            const plan = pricingStore.getCurrentPlan(data.billing);
            const chats = data.features?.chatsPerMonth ?? plan.chatsPerMonth;
            const lessons = data.features?.lessonsPerMonth ?? plan.lessonsPerMonth;
            limits.value = chats != null && lessons != null ? { chats, lessons } : null;
            if (limits.value === null) {
              // Plan lapsed mid-session: clear so a later re-upgrade refetches.
              usageRequested = false;
              usage.value = null;
              return;
            }
            // Re-fetch when the stored timezone changes: the month boundary
            // moves with it.
            if (usageRequested && timezone.value === fetchedTimezone) return;
            usageRequested = true;
            fetchedTimezone = timezone.value;
            const token = ++fetchToken;
            // Month boundary in the user's stored timezone, matching the
            // server's counting window (helpers/dialog.countDialogsThisMonth).
            fetchMonthlyUsage(userId, data.timezone)
              .then((result) => {
                if (token === fetchToken) {
                  usage.value = result;
                  failed.value = false;
                }
              })
              .catch((error) => {
                // Never block the account page over a meter; just hide it,
                // and allow the next snapshot to retry.
                console.error("Usage meter read failed:", error);
                usageRequested = false;
                if (token === fetchToken) failed.value = true;
              });
          },
          () => (failed.value = true)
        );
      },
      { immediate: true }
    );
    onUnmounted(() => {
      if (off) off();
      stopUserWatch();
    });

    // Paid users only: free tier has lifetime limits with their own messaging.
    const isVisible = computed(
      () => !failed.value && usage.value !== null && limits.value !== null
    );

    const rows = computed(() => [
      {
        key: "chats",
        labelKey: "usageMeter.chatsLeft",
        limit: limits.value?.chats ?? 0,
        remaining: Math.max(0, (limits.value?.chats ?? 0) - (usage.value?.chats ?? 0)),
      },
      {
        key: "lessons",
        labelKey: "usageMeter.lessonsLeft",
        limit: limits.value?.lessons ?? 0,
        remaining: Math.max(0, (limits.value?.lessons ?? 0) - (usage.value?.lessons ?? 0)),
      },
    ]);

    // Next month boundary in the same stored timezone the usage window uses,
    // formatted in the app locale (not the browser defaults for either).
    const resetDate = computed(() => {
      const { year, month } = getCurrentYearMonthInTimezone(timezone.value);
      // month is 1-based, so passing it as a 0-based index yields next month.
      return new Date(year, month, 1).toLocaleDateString(locale.value || undefined);
    });

    return { isVisible, rows, resetDate };
  },
};
</script>

<style scoped>
.usage-meter {
  padding: 12px 16px;
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius);
  background: var(--al-surface);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.meter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--al-text);
}
.meter-row.warning {
  color: var(--al-warning);
  font-weight: 600;
}
.meter-reset {
  color: var(--al-text-muted);
  font-size: 0.85rem;
}
</style>
